// Splits measured blocks into pages. Heights are in points; nothing here touches the DOM.

export type Measured =
  | { kind: "fixed"; index: number; background: string }
  | { kind: "break"; background: string }
  | {
      kind: "lines";
      index: number;
      lines: number;
      leading: number;
      // A band's padding, above and below the lines of each page's part.
      padding?: number;
      spaceBefore: number;
      spaceAfter: number;
    }
  | {
      kind: "whole";
      index: number;
      height: number;
      spaceBefore: number;
      spaceAfter: number;
      keepSpace?: number;
      pageHeading?: boolean;
    }
  | {
      kind: "rows";
      index: number;
      header: number;
      rows: number[];
      total: number;
      spaceBefore: number;
      spaceAfter: number;
    }
  | {
      kind: "items";
      index: number;
      items: number[];
      itemSpace: number;
      spaceBefore: number;
      spaceAfter: number;
    };

export type Fragment =
  | { kind: "whole"; index: number; top: number }
  | { kind: "lines"; index: number; top: number; from: number; to: number }
  | {
      kind: "rows";
      index: number;
      top: number;
      from: number;
      to: number;
      total: boolean;
    }
  | { kind: "items"; index: number; top: number; from: number; to: number };

export interface Page {
  background: string;
  fixedIndex?: number;
  fragments: Fragment[];
}

// Fewest lines a paragraph may leave at the bottom of a page.
const MIN_LINES_BEFORE_BREAK = 2;

export const paginate = (blocks: Measured[], frameHeight: number): Page[] => {
  const pages: Page[] = [];
  let page: Page | undefined;
  let y = 0;
  let lastSpaceAfter = 0;
  let afterPageHeading = false;

  const newPage = (background: string) => {
    page = { background, fragments: [] };
    pages.push(page);
    y = 0;
    lastSpaceAfter = 0;
  };
  const current = (): Page => {
    if (!page) newPage("content");
    return page as Page;
  };
  // Adjacent spaces collapse to the larger one, which is where the original PDF
  // places every block; nothing is added at the top of a page.
  const gap = (spaceBefore: number) =>
    y === 0 ? 0 : Math.max(lastSpaceAfter, spaceBefore);

  for (const block of blocks) {
    if (block.kind === "fixed") {
      newPage(block.background);
      current().fixedIndex = block.index;
      page = undefined;
      continue;
    }
    if (block.kind === "break") {
      newPage(block.background);
      afterPageHeading = false;
      continue;
    }

    if (block.kind === "whole") {
      const before = gap(block.spaceBefore);
      // A heading moves on when too little is left under it, unless it follows a page heading.
      const tooLow =
        block.keepSpace !== undefined &&
        !afterPageHeading &&
        frameHeight - y - before < block.keepSpace;
      if (y > 0 && (tooLow || y + before + block.height > frameHeight)) {
        newPage(current().background);
      }
      y += gap(block.spaceBefore);
      current().fragments.push({ kind: "whole", index: block.index, top: y });
      y += block.height;
      lastSpaceAfter = block.spaceAfter;
      afterPageHeading = Boolean(block.pageHeading);
      continue;
    }
    afterPageHeading = false;

    if (block.kind === "lines") {
      let from = 0;
      const band = 2 * (block.padding ?? 0);
      y += gap(block.spaceBefore);
      while (from < block.lines) {
        const fits = Math.floor(
          (frameHeight - y - band + 0.01) / block.leading
        );
        // An empty page always takes at least a line, so a line taller than the frame can't loop forever.
        const fit = y === 0 && !(fits > 0) ? 1 : fits;
        const remaining = block.lines - from;
        if (fit >= remaining) {
          current().fragments.push({
            kind: "lines",
            index: block.index,
            top: y,
            from,
            to: block.lines
          });
          y += remaining * block.leading + band;
          from = block.lines;
        } else if (fit >= MIN_LINES_BEFORE_BREAK || (y === 0 && fit > 0)) {
          current().fragments.push({
            kind: "lines",
            index: block.index,
            top: y,
            from,
            to: from + fit
          });
          from += fit;
          newPage(current().background);
        } else {
          newPage(current().background);
        }
      }
      lastSpaceAfter = block.spaceAfter;
      continue;
    }

    if (block.kind === "rows") {
      const { header, rows, total } = block;
      y += gap(block.spaceBefore);
      // A table starts only where its header and first row fit, with the
      // total too when that row is also the last.
      const first = rows.length > 1 ? rows[0] : (rows[0] ?? 0) + total;
      if (y > 0 && y + header + first > frameHeight)
        newPage(current().background);
      let from = 0;
      let top = y;
      y += header;
      let row = 0;
      while (row < rows.length) {
        // The last row stays only where the total fits under it.
        const needed = rows[row] + (row === rows.length - 1 ? total : 0);
        if (y + needed > frameHeight && row > from) {
          current().fragments.push({
            kind: "rows",
            index: block.index,
            top,
            from,
            to: row,
            total: false
          });
          newPage(current().background);
          from = row;
          top = 0;
          y = header;
        }
        y += rows[row];
        row += 1;
      }
      current().fragments.push({
        kind: "rows",
        index: block.index,
        top,
        from,
        to: rows.length,
        total: true
      });
      y += total;
      lastSpaceAfter = block.spaceAfter;
      continue;
    }

    // Bullets break between items.
    let from = 0;
    y += gap(block.spaceBefore);
    let top = y;
    block.items.forEach((height, item) => {
      const space = item > from ? block.itemSpace : 0;
      if (y + space + height > frameHeight && y > 0) {
        if (item > from) {
          current().fragments.push({
            kind: "items",
            index: block.index,
            top,
            from,
            to: item
          });
        }
        newPage(current().background);
        from = item;
        top = 0;
        y = height;
        return;
      }
      y += space + height;
    });
    current().fragments.push({
      kind: "items",
      index: block.index,
      top,
      from,
      to: block.items.length
    });
    lastSpaceAfter = block.spaceAfter;
  }
  return pages;
};
